<template>
  <div>
    <h3 class="title is-3">Games</h3>

    <button class="button is-primary" @click="openGameNewCreator">Create a game</button>

    <p>&nbsp;</p>

    <div class="block">
      <b-field grouped>
        <b-input
            v-model="searchFilter"
            expanded
            icon="magnify"
            placeholder="Search for games..."
            @input="searchFilterChanged">
        </b-input>
        <b-checkbox-button v-for="(gameStatus, index) in gameStatusesArray"
                           :key="index"
                           v-model="gameStatusesFilter"
                           :expanded="false"
                           :native-value="gameStatus"
                           style="padding-right: 4px"
                           type="is-info"
                           @input="searchFilterChanged"
        >
          <b-icon icon="check"></b-icon>
          <span>{{ gameStatus }}</span>
        </b-checkbox-button>
      </b-field>
    </div>

    <div>
      <b-select v-model="gameActions" @input="option => gameActionSelected(option)">
        <option selected value="select">Actions</option>
        <option :disabled="checkedRows.length === 0" value="start">Start</option>
        <option :disabled="checkedRows.length === 0" value="reset">Reset</option>
        <option :disabled="checkedRows.length === 0" value="pause">Pause</option>
        <option :disabled="checkedRows.length === 0" value="stop">Stop</option>
        <option :disabled="checkedRows.length === 0" value="declone">Declone</option>
        <option :disabled="checkedRows.length === 0" value="archive">Archive</option>
      </b-select>
    </div>
    <b-table v-if="games"
             :checked-rows.sync="checkedRows"
             :data="gamesFiltered"
             :default-sort="['gameStartRawDate', 'desc']"
             :hoverable="true"
             :loading="isLoading"
             :paginated="true"
             :pagination-simple="true"
             :per-page="50"
             :row-class="(row, index) => getRowClassWhenSelected(row, index)"
             :striped="true"
             checkable
    >
      <template slot-scope="props">
        <b-table-column class="centered-table-content" field="gameStatus" label="Status" sortable>
          <game-status-tag :game="props.row"/>
        </b-table-column>

        <b-table-column class="centered-table-content" field="gameStyle" sortable width="120px">
          <span style="text-align: center">
            <b-tooltip :label="'This is a ' + props.row.gameStyle + ' game' + showMaxDuration(props.row)">
              <b-icon :icon="isThereAnIconForGameStyle(props.row.gameStyle)" type="is-dark"></b-icon>
            </b-tooltip>
          </span>
          <span style="text-align: center">
            <b-tooltip :label="'This game is also limited in time: ' + props.row.maxDurationInMinutes + ' min'">
              <b-icon v-if="addIconForTimeAttack(props.row)" icon="clock-fast"
                      type="is-dark"></b-icon>
            </b-tooltip>
          </span>
        </b-table-column>

        <b-table-column class="centered-table-content" field="gameName" label="Game name" sortable width="220px">
          <b-tooltip :label="props.row.gameDescription">{{ props.row.gameName }}</b-tooltip>
          <span v-if="hasTuto(props.row)" style="margin-left: 3px">
            <b-tooltip label="This game has an intro">
              <b-icon icon="school" size="is-small" type="is-success"></b-icon>
            </b-tooltip>
          </span>
        </b-table-column>

        <b-table-column class="centered-table-content" field="pinCode" label="Pin" width="150px">
          <span style="background-color: beige"><strong>{{ props.row.pinCode }}</strong>&nbsp;</span>
          <span v-if="props.row.accessType === 'public'" style="margin-left: 3px">
              <b-tooltip label="This game is public">
                <b-icon icon="earth" size="is-small" type="is-dark"></b-icon>
              </b-tooltip>
          </span>
        </b-table-column>

        <b-table-column class="centered-table-content" field="map" label="Map/Pack" sortable width="170px">
          {{ props.row.wizardInformation ? props.row.wizardInformation.map.mapName : '–' }}
          <span>
                        <b-tooltip label="This game has been created en masse">
                            <b-icon v-if="props.row.groupId" icon="checkbox-multiple-marked-outline" size="is-small"
                                    type="is-dark"></b-icon>
                        </b-tooltip>
                    </span>
          <br/>
          <span style="font-size: small">{{
              props.row.wizardInformation ? props.row.wizardInformation.chosenPack.packName : '–'
            }}</span>
        </b-table-column>

        <b-table-column class="centered-table-content" field="gameQuestionsNumber" label="Challenges" numeric
                        sortable>
          {{ props.row.questionCount }}
          <span>
                        <b-tooltip label="This game has been created by the wizard">
                            <b-icon v-if="isCreatedFromWizard(props.row)" icon="flash" size="is-small"
                                    type="is-warning"></b-icon>
                        </b-tooltip>
                    </span>
        </b-table-column>

        <b-table-column class="centered-table-content" field="gameTeamsNumber" label="Teams" numeric sortable>
          {{ props.row.teamCount }}
          <span>
                        <b-tooltip label="Players can create teams">
                            <b-icon v-if="canPlayersCanCreateTeams(props.row)" icon="plus-circle-outline"
                                    size="is-small"
                                    type="is-success"></b-icon>
                        </b-tooltip>
                    </span>
        </b-table-column>

        <b-table-column class="centered-table-content" field="gameStartRawDate" label="Start" sortable
                        style="font-size: 0.8em">
          <b-tooltip :label="props.row.gameStartDate">
            {{ props.row.gameStartDate.substring(0, 11) }}
          </b-tooltip>
        </b-table-column>

        <b-table-column class="centered-table-content" field="gameEndRawDate" label="End" sortable
                        style="font-size: 0.8em">
          <b-tooltip :label="props.row.gameEndDate">
            {{ props.row.gameEndDate.substring(0, 11) }}
          </b-tooltip>
        </b-table-column>

        <b-table-column label="Manage" width="400">
          <game-manage-panel
              :game="props.row"
              @triggerConfirmArchiveGame="confirmArchiveGame"
              @triggerConfirmPauseGame="confirmPauseGame"
              @triggerConfirmResetGame="confirmResetGame"
              @triggerConfirmResumeGame="confirmResumeGame"
              @triggerConfirmStartGame="confirmStartGame"
              @triggerConfirmStopGame="confirmStopGame"
              @triggerDecloneGame="manageDecloneGame"
              @triggerDisableLeaderboard="manageLeaderBoardState"
              @triggerEditGame="editGame"
              @triggerGenerateNewPin="manageGenerateNewPin"
              @triggerManageTeams="manageTeams"
              @triggerPreviewGame="previewGame"
              @triggerShowDuplicateGame="showDuplicateGame"
              @triggerShowGameCompletion="showGameCompletion"
              @triggerShowGameSummary="showGameSummary"
              @triggerShowLeaderboard="showLeaderboard"
          />
        </b-table-column>
      </template>
    </b-table>

    <b-modal :active.sync="isDuplicatePanelActive"
             :destroy-on-hide="true"
             has-modal-card
             scroll="keep"
             trap-focus>
      <duplicate-game-in-step :key="duplicateGameModalKey" :game="gameToDuplicate"
                              @finishDuplicate="loadGameList"></duplicate-game-in-step>
    </b-modal>

    <b-loading :active.sync="isLoading" :canCancel="true" :is-full-page="true"></b-loading>
  </div>
</template>
<script>
import _ from 'lodash'
import gamesService from '../services/gamesService'
import dateUtils from '../helpers/date-helper'
import gameHelper from '../helpers/game-helper'
import textutils from '../helpers/text-helper'
import leaderboardService from '../services/leaderboard.service'
// eslint-disable-next-line no-unused-vars
import {DialogProgrammatic as Dialog} from 'buefy'
import GameStatusTag from './single/GameStatusTag.vue'
import GameTimer from './single/GameTimer.vue'
import GameManagePanel from './single/GameManagePanel.vue'
import displayHelper from '../helpers/display-helper'
import DuplicateGameInStep from './games/DuplicateGameInStep'
import GAME_STATUS from '@/enums/gameStatus'
import gameStylesHelper from '@/enums/gameStyles'

const MESSAGE_PAUSE_ALL = 'Are you sure you want to pause these games?'
const MESSAGE_START_ALL = 'Are you sure you want to start these games?'
const MESSAGE_STOP_ALL = 'Are you sure you want to stop these games?'
const MESSAGE_DECLONE_ALL = 'Are you sure you want to de-clone these games?'
const MESSAGE_ARCHIVE_ALL = 'Are you sure you want to archive these games?'
const MESSAGE_RESET_ALL = 'Are you sure you want to reset these games?<br/>' +
    'All answers will be deleted.<br/><br/>' +
    'The devices won\'t automatically be reset, you will need to reset them manually in the Teams submenu'

export default {
  components: {
    GameManagePanel,
    GameTimer,
    GameStatusTag,
    DuplicateGameInStep,
  },
  name: 'Games',
  created() {
    const parent = this
    this.gameStatusesArray = Object.values(GAME_STATUS).sort()
    this.gameStatusesFilter = this.gameStatusesArray

    this.loadGameList()
        .then(() => {
          console.log('Displaying games')
        })
        .catch((err) => {
          displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
        })
  },
  data() {
    return {
      isLoading: false,
      isFullPageLoading: true,
      games: null,
      isDuplicatePanelActive: false,
      gameToDuplicate: null,
      duplicateGameModalKey: 0,
      gamesFiltered: [],
      gameStatusesArray: [],
      gameStatusesFilter: [],
      searchFilter: null,
      checkedRows: [],
      gameActions: 'select',
      columns: [
        {field: 'gameName'},
        {field: 'map'},
        {field: 'gameDescription'},
        {field: 'pack'},
      ],
    }
  },
  filters: {
    truncate(value, length) {
      return textutils.truncate(value, length)
    },
  },
  methods: {
    isThereAnIconForGameStyle: gameStylesHelper.isThereAnIconForGameStyle,

    isCreatedFromWizard(game) {
      return _.get(game, 'wizardInformation.creationMode') === 'Wizard'
    },
    gameActionSelected(option) {
      if (option === 'select') {
        return
      }
      const dialogConfigs = {
        start: {
          message: MESSAGE_START_ALL,
          guardFunction: (game) => gameHelper.isGameNotActive(game) && !gameHelper.isGameNotSetUp(game) && !gameHelper.isGameStopped(game),
          actionFunction: (game) => this.startGame(game)
        },
        reset: {
          message: MESSAGE_RESET_ALL,
          guardFunction: (game) => gameHelper.isGamePlaying(game),
          actionFunction: (game) => this.resetGame(game)
        },
        pause: {
          message: MESSAGE_PAUSE_ALL,
          guardFunction: (game) => gameHelper.isGamePlaying(game),
          actionFunction: (game) => this.pauseGame(game)
        },
        stop: {
          message: MESSAGE_STOP_ALL,
          guardFunction: (game) => gameHelper.isGamePlaying(game),
          actionFunction: (game) => this.stopGame(game)
        },
        declone: {
          message: MESSAGE_DECLONE_ALL,
          guardFunction: (game) => !gameHelper.isGameCloned(game),
          actionFunction: (game) => this.deCloneGame(game)
        },
        archive: {
          message: MESSAGE_ARCHIVE_ALL,
          guardFunction: (game) => !gameHelper.isGamePlaying(game),
          actionFunction: (game) => this.archiveGame(game)
        },
      }
      if (!dialogConfigs[option]) {
        console.log('ERROR: unknown option: ' + option)
        return
      }
      const dialogConfig = dialogConfigs[option]
      Dialog.confirm({
        message: dialogConfig.message,
        onConfirm: () => {
          const promises = _.map(this.checkedRows, (game) => {
            if (dialogConfig.guardFunction(game)) {
              console.log(`this game is valid for ${option} = ${game.gameName} `)
              return dialogConfig.actionFunction(game)
            }
            return Promise.resolve()
          })
          return Promise.all(promises)
              .then(() => {
                this.searchFilterChanged()
                this.checkedRows = []
                this.gameActions = 'select'
              })
        }
      })
    },
    confirmArchiveGame(game) {
      Dialog.confirm({
        message: 'Are you sure you want to archive this game?',
        onConfirm: () => this.archiveGame(game)
      })
    },
    confirmStopGame(game) {
      Dialog.confirm({
        message: 'Are you sure you want to stop this game?',
        onConfirm: () => this.stopGame(game)
      })
      this.searchFilterChanged()
    },
    confirmStartGame(game) {
      Dialog.confirm({
        message: 'Are you sure you want to start this game?',
        onConfirm: () => this.startGame(game)
      })
      this.searchFilterChanged()
    },
    confirmPauseGame(game) {
      console.log(game)
      Dialog.confirm({
        message: 'Are you sure you want to pause this game?',
        onConfirm: () => this.pauseGame(game)
      })
      this.searchFilterChanged()
    },
    confirmResumeGame(game) {
      Dialog.confirm({
        message: 'Are you sure you want to resume this game?',
        onConfirm: () => this.resumeGame(game)
      })
      this.searchFilterChanged()
    },
    confirmResetGame(game) {
      Dialog.confirm({
        message: 'Are you sure you want to reset the game? All answers will be deleted. The devices won\'t be reset automatically, you have to reset them manually if needed.',
        onConfirm: () => this.resetGame(game)
      })
    },
    showGameCompletion(game) {
      this.$router.push({name: 'GameCompletion', params: {id: game.idGame, name: game.gameName}})
    },
    showLeaderboard(game) {
      this.$router.push({name: 'Leaderboard', params: {id: game.idGame, name: game.gameName}})
    },
    showGameSummary(game) {
      this.$router.push({name: 'GameSummary', params: {id: game.idGame, name: game.gameName}})
    },
    sortByEndDate(a, b, isAsc) {
      return dateUtils.isDate1BeforeDate2(a.gameEndDate, b.gameEndDate, isAsc)
    },
    archiveGame(game) {
      this.isLoading = true
      const parent = this
      return gamesService.archiveGame(game.idGame)
          .then(() => {
            displayHelper.displaySuccess(parent, `The game "${game.gameName}" has been archived.`)
            const gameIndex = this.games.indexOf(game)
            if (gameIndex >= 0) {
              this.games.splice(gameIndex, 1)
            }
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
          })
          .finally(() => {
            this.goToTop()
            this.searchFilterChanged()
            this.isLoading = false
          })
    },
    startGame(game) {
      const parent = this
      this.isLoading = true
      gamesService.startGame(game.idGame)
          .then(() => {
            return this.loadGameList()
          })
          .catch((err) => {
            this.loadGameList()
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
          })
          .finally(() => {
            this.searchFilterChanged()
            this.isLoading = false
          })
    },
    pauseGame(game) {
      const parent = this
      this.isLoading = true
      gamesService.pauseGame(game.idGame)
          .then(() => {
            return this.loadGameList()
          })
          .catch((err) => {
            this.loadGameList()
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
          })
          .finally(() => {
            this.searchFilterChanged()
            this.isLoading = false
          })
    },
    resumeGame(game) {
      const parent = this
      this.isLoading = true
      gamesService.resumeGame(game.idGame)
          .then(() => {
            return this.loadGameList()
          })
          .catch((err) => {
            this.loadGameList()
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
          })
          .finally(() => {
            this.searchFilterChanged()
            this.isLoading = false
          })
    },
    stopGame(game) {
      const parent = this
      this.isLoading = true
      const gameLength = gameHelper.createFormattedElapsedTime(game.timer)
      gamesService.stopGame(game.idGame, gameLength)
          .then(() => {
            return this.loadGameList()
          })
          .catch((err) => {
            this.loadGameList()
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
          })
          .finally(() => {
            this.searchFilterChanged()
            this.isLoading = false
          })
    },
    resetGame(game) {
      const parent = this
      this.isLoading = true
      return gamesService.resetGame(game.idGame)
          .then(() => {
            displayHelper.displaySuccess(parent, `The game "${game.gameName}" has been successfully reset.`)
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
          })
          .finally(() => {
            this.goToTop()
            this.searchFilterChanged()
            this.isLoading = false
          })
    },
    editGame(game) {
      if (_.get(game, 'wizardInformation.creationMode') === 'Wizard') {
        this.$router.push({name: 'EditGameInSteps', params: {id: game.idGame}})
      } else {
        this.$router.push({name: 'EditGames', params: {id: game.idGame}})
      }
    },
    previewGame(game) {
      this.$router.push({name: 'PreviewGame', params: {id: game.idGame}})
    },
    manageTeams(game) {
      this.$router.push({name: 'Teams', params: {id: game.idGame}})
    },
    manageGenerateNewPin(game) {
      this.isLoading = true
      const parent = this
      return gamesService.generateNewPin(game.idGame)
          .then(() => {
            displayHelper.displaySuccess(parent, `The PIN of game "${game.gameName}" has been changed.`)
            this.goToTop()
            this.isLoading = false
            this.$router.go()
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
            this.goToTop()
            this.isLoading = false
          })
    },
    manageDecloneGame(game) {
      this.isLoading = true
      const parent = this
      return gamesService.deCloneGame(game.idGame)
          .then(() => {
            displayHelper.displaySuccess(parent, `The game "${game.gameName}" has been de-cloned`)
            this.goToTop()
            this.isLoading = false
            this.$router.go()
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops, there was an error: ' + err.message)
            this.goToTop()
            this.isLoading = false
          })
    },
    manageLeaderBoardState(disableLeaderBoard, game) {
      const parent = this
      return leaderboardService.changeLeaderBoard(game.idGame, disableLeaderBoard)
          .then((updatedGame) => {
            this.games = this.games.map((game) => {
              if (game.idGame === updatedGame.idGame) {
                game.isLeaderDisabled = updatedGame.isLeaderDisabled
              }
              return game
            })
          })
          .then(() => {
            const disableOrEnabled = disableLeaderBoard ? 'disabled' : 'enabled'
            displayHelper.displaySuccess(parent, `The leaderBoard for "${game.gameName}" has been successfully ${disableOrEnabled} `)
          })
          .catch(() => {
            displayHelper.displayError(parent, 'There was an error trying to update leaderboard status')
          })
          .finally(() => {
            this.goToTop()
          })
    },
    openGameNewCreator() {
      this.$router.push({name: 'CreateGameInSteps'})
    },
    hasTuto(game) {
      return !!game.introTextId
    },
    showDuplicateGame(game) {
      if (_.get(game, 'wizardInformation.creationMode') === 'Wizard') {
        this.gameToDuplicate = game
        this.duplicateGameModalKey++ // force refresh
        this.isDuplicatePanelActive = true
      } else {
        this.$router.push({name: 'DuplicateGames', params: {id: game.idGame, name: game.gameName}})
      }
    },
    loadGameList() {
      this.isLoading = true
      const parent = this
      return gamesService.getGames().then((games) => {
        this.games = gameHelper.formatGames(games)
        this.gamesFiltered = this.games
        this.searchFilterChanged()
        this.isLoading = false
      })
          .catch((error) => {
            displayHelper.displayError(parent, 'Oops, there was an error while listing the games: ' + error.message)
            this.isLoading = false
          })
    },
    canPlayersCanCreateTeams(game) {
      return game.playerCanAddTeams === true
    },
    searchFilterChanged() {
      if (this.searchFilter && this.searchFilter !== '') {
        let searchLower = this.searchFilter.toLowerCase()
        this.gamesFiltered = _.filter(this.games, r => {
          let found = false
          for (let col of this.columns) {
            let theRow = r[col.field]

            if (col.field === 'pack') {
              if (r.wizardInformation && r.wizardInformation.chosenPack) {
                theRow = r.wizardInformation.chosenPack.packName
              }
            }

            if (col.field === 'map') {
              if (r.wizardInformation && r.wizardInformation.map) {
                theRow = r.wizardInformation.map.mapName
              }
            }

            if (theRow) {
              found = theRow.toString().toLowerCase().indexOf(searchLower) >= 0 &&
                  this.includeGameStatusFilter(r['gameStatus'])
              if (found) {
                return true
              }
            }
          }
          return found
        })
      } else {
        this.gamesFiltered = _.filter(this.games, r => {
          return this.includeGameStatusFilter(r['gameStatus'])
        })
      }
    },
    includeGameStatusFilter(status) {
      return this.gameStatusesFilter.includes(status)
    },
    goToTop() {
      window.scrollTo(0, 0)
    },
    getRowClassWhenSelected(game, index) {
      if (this.checkedRows.includes(game)) {
        return 'is-grounched'
      }
    },
    addIconForTimeAttack(game) {
      return game.gameStyle !== gameStylesHelper.GAME_STYLES.TIME_ATTACK && game.maxDurationInMinutes > 0
    },
    showMaxDuration(game) {
      if (game.gameStyle === gameStylesHelper.GAME_STYLES.TIME_ATTACK) {
        return ' (' + game.maxDurationInMinutes + ' min)'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.button {
  margin: 2px;
}

.game-form label {
  margin-bottom: 0;
  width: 180px !important;
}

.game-form .column .field {
  align-items: center;
}

.date-time /deep/ {
  z-index: 15 !important;
}

.picture-input /deep/ .preview-container {
  margin: 0;
  z-index: 1 !important;
}

.picture-input /deep/ .picture-preview {
  margin: 0;
  z-index: 1 !important;
}

.picture-input /deep/ .picture-inner {
  z-index: 1 !important;
}

.picture-input /deep/ .preview-container {
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.centered-table-content {
  vertical-align: middle;
}
</style>

<style>
tr.is-grounched {
  background: #bcd3f6 !important;
}
</style>
